import React, { useMemo } from 'react';

import { useMy } from '@/hooks/useMy';
import { CollaboratingWith } from '@/sections/CollaboratingWith';
import { SettingDisplayCard } from '@/sections/settings/components';
import { Users } from '@/sections/Users';
import { useAuth } from '@/state/auth';
import { Account } from '@/types';

interface UserManagementCardsProps {
  /**
   * Email for impersonation - if provided, will fetch data for this user's account.
   * If not provided, uses the current authenticated user's account.
   *
   * IMPORTANT: This component RESPECTS impersonation, unlike UserProfileCards.
   * When impersonating, shows user tables for the impersonated account.
   *
   * Note: The "Add User" button is now handled by UserManagementTab, not this component.
   */
  email?: string;
}

/**
 * UserManagementCards displays user management settings including
 * authorized users and collaborator relationships.
 *
 * IMPORTANT: This component RESPECTS impersonation.
 * When impersonating, shows user tables for the impersonated account.
 *
 * Difference from UserProfileCards:
 * - UserProfileCards = MY settings (ignores impersonation)
 * - UserManagementCards = User tables for CURRENT CONTEXT (respects impersonation)
 *
 * Cards:
 * 1. Authorized Users - Users with access to the account, with "Add User" action
 * 2. Collaborating With - Organizations the user collaborates with (only shown for own account)
 *
 * Pattern Compliance:
 * - Single subsection header for logical grouping (h3)
 * - Unified card layout with gap-2 spacing
 * - Complete data-testid coverage for E2E testing
 *
 * React 19 Features:
 * - Standard caching strategy with TanStack Query
 * - No unnecessary memoization (React Compiler handles optimization)
 *
 * @see {@link UserProfileCards} - For profile settings (used by Profile page)
 * @see {@link UserManagementTab} - Settings tab that renders this component
 */
export const UserManagementCards: React.FC<UserManagementCardsProps> = ({
  email,
}) => {
  const { me, friend } = useAuth();
  const currentEmail = email || friend || me;

  // Accounts data for collaborators - fetch for current context
  const { data: accounts } = useMy(
    {
      resource: 'account',
    },
    {
      email: currentEmail,
      staleTime: 30000,
      gcTime: 300000,
    }
  );

  // Extract collaborators from accounts
  const collaborators = useMemo(() => {
    return accounts
      ?.filter(
        (acc: Account) =>
          !acc.key.endsWith('#settings#') && acc?.member === acc?.username
      )
      .map((acc: Account) => acc.name);
  }, [accounts]);

  // Determine if we're viewing our own account (not impersonating)
  // Collaborating With card only shows for own account
  const isOwnAccount = !friend || currentEmail === me;

  return (
    <div className="flex flex-col gap-4" data-testid="user-management-cards">
      {/* NOTE: Section header "User Management" and "Add User" button are rendered by UserManagementTab.tsx
          This component only renders the user tables and collaborating with card */}

      {/* Users tables - each table type has its own styled card with header */}
      <Users customerEmail={email} variant="compact" />

      {/* Collaborating With Card - Only show if collaborators exist and viewing own account */}
      {collaborators && collaborators.length > 0 && isOwnAccount && (
        <SettingDisplayCard data-testid="collaborating-with-card">
          <div className="flex w-full flex-col gap-4">
            {/* Header */}
            <div className="flex flex-col gap-1">
              <h4 className="font-bold">Collaborating With</h4>
              <span className="text-chariot-text-secondary text-xs">
                These organizations have invited you to view their account
                details. You are currently viewing{' '}
                <span className="text-chariot-brand-primary font-bold">
                  OPEN
                </span>{' '}
                vulnerabilities.
              </span>
            </div>

            {/* Collaborators Table */}
            <CollaboratingWith />
          </div>
        </SettingDisplayCard>
      )}
    </div>
  );
};
